import { Buffer } from 'buffer';
import process from 'process';
import './DisableOverlay'; // 👈 must be first

import React from 'react';
// import ReactDOM from 'react-dom';
import 'antd/dist/antd.css';
// Content render styles for stored rich-text (.fr-view) — in-house replacement
// for editor_style.min.css. Keeps legacy notes rendering identically at the
// read-only display sites (dangerouslySetInnerHTML).
import './Common/EzyEditor/content-styles.css';
import 'braft-editor/dist/index.css'; //  Introducing styles
import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';
import createSagaMiddleware from 'redux-saga';
import { createStore, applyMiddleware } from 'redux';
import { Provider } from 'react-redux';
import { logger } from 'redux-logger';
import reducer from './Redux/reducers';
import rootSaga from './Sagas';
import ErrorBoundary from './ErrorBoundary';
import { maybeHandBackToNewUi, listenForNewUiSession } from './Common/newUiRedirect';
import ReactDOM from 'react-dom/client'; // ✅ not 'react-dom'

const container = document.getElementById('root');
const root = ReactDOM.createRoot(container);

window.Buffer = Buffer;
window.process = process;

// ✅ Patch ResizeObserver globally to suppress loop errors
(function patchResizeObserver() {
  if (typeof ResizeObserver === 'undefined') return;

  const OriginalResizeObserver = window.ResizeObserver;

  class SafeResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      const safeCallback = (...args) => {
        try {
          callback(...args);
        } catch (err) {
          if (
            err?.message?.includes('ResizeObserver loop limit exceeded') ||
            err?.message?.includes('ResizeObserver loop completed with undelivered notifications.')
          ) {
            // Suppress silently
            return;
          }
          throw err;
        }
      };
      super(safeCallback);
    }
  }

  window.ResizeObserver = SafeResizeObserver;
})();

// ✅ Suppress Dev Overlay (Red Screen) for ResizeObserver errors
window.addEventListener('error', (e) => {
  if (
    e.message === 'ResizeObserver loop limit exceeded' ||
    e.message === 'ResizeObserver loop completed with undelivered notifications.'
  ) {
    e.stopImmediatePropagation();
  }
});

window.addEventListener('unhandledrejection', (e) => {
  const message = e.reason?.message || '';
  if (
    message.includes('ResizeObserver loop limit exceeded') ||
    message.includes('ResizeObserver loop completed with undelivered notifications.')
  ) {
    e.stopImmediatePropagation();
  }
});

// ✅ Suppress console.error messages for ResizeObserver
const originalConsoleError = console.error;
console.error = (...args) => {
  if (
    typeof args[0] === 'string' &&
    (args[0].includes('ResizeObserver loop limit exceeded') ||
      args[0].includes('ResizeObserver loop completed with undelivered notifications.'))
  ) {
    return;
  }

  originalConsoleError(...args);
};

// ✅ Redux setup
const sagaMiddleware = createSagaMiddleware();
const store = createStore(reducer, applyMiddleware(sagaMiddleware, logger));
sagaMiddleware.run(rootSaga);

// The New UI's "old UI" switch posts its session in once this window opens, so
// the listener has to exist before the first render — it starts sending
// immediately and anything installed later misses those sends.
listenForNewUiSession();

// Loader for the hand-back path below, where nothing is rendered at all.
//
// Written straight into #root as DOM rather than rendered by React, on purpose:
// the whole point of that path is that React never mounts, so no legacy screen
// can flash on the way out. Mounting a React root just to draw a spinner would
// pull `<Provider>`/`<App />` back into the decision and re-open exactly the
// flash this was written to avoid, for no gain — this markup is static.
//
// The markup is antd's own Spin output for `<Spin size="large" tip="Loading..." />`
// (see antd/lib/spin — `ant-spin-lg`, `-spinning` for the visible state,
// `-show-text` for the tip), so it is styled by `antd/dist/antd.css` — imported
// at the top of this file, so it is in the document before this runs — and
// comes out pixel-identical to the hand-off loader in Login.js
// (getHandoffLoader) without a line of new CSS. Both hand-off paths therefore
// look the same. If antd ever renames these classes the worst case is a plain
// white page with "Loading...", which is still better than today's blank.
function paintHandbackLoader() {
  if (!container) return;
  container.innerHTML =
    '<div style="position:fixed;top:0;left:0;right:0;bottom:0;z-index:9999;' +
    'background-color:#FFFFFF;display:flex;justify-content:center;align-items:center;">' +
    '<div class="ant-spin ant-spin-lg ant-spin-spinning ant-spin-show-text" aria-live="polite" aria-busy="true">' +
    '<span class="ant-spin-dot ant-spin-dot-spin">' +
    '<i class="ant-spin-dot-item"></i><i class="ant-spin-dot-item"></i>' +
    '<i class="ant-spin-dot-item"></i><i class="ant-spin-dot-item"></i>' +
    '</span>' +
    '<div class="ant-spin-text">Loading...</div>' +
    '</div></div>';
}

// The New UI owns the session once login has handed it over. If this app is
// re-opened while that session is still good, hand it straight back instead of
// rendering the legacy shell — checked BEFORE the first render so no legacy
// screen flashes on the way out. Returns true once it is navigating away.
if (!maybeHandBackToNewUi()) {
  // ✅ React render
  root.render(
    <Provider store={store}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </Provider>,
    document.getElementById('root')
  );
} else {
  // Navigating away — either a hand-back form POST to the New UI, or the
  // ?signedout=1 redirect to /login. Still deliberately NOT rendering the app,
  // but the tab keeps showing this document until that navigation lands,
  // and #root is empty (`<span></span>` from index.html), so without this the
  // user watches a blank page for the whole round trip.
  //
  // Painted after maybeHandBackToNewUi() rather than before, because that
  // function is what decides whether we hand back at all and it submits the
  // form itself — its return semantics are the two-app contract and are left
  // alone. form.submit() only queues the navigation, so this still paints on
  // the very next frame, long before a cross-origin POST can come back.
  paintHandbackLoader();
}

// ✅ Optional: service worker
serviceWorker.unregister();
